import React, { useMemo, useState } from "react";
import {
  ButtonBase,
  Popover,
  Typography,
  IconButton,
  Divider,
  InputBase,
  makeStyles,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { Check, Search, X } from "lucide-react";
const useStyles = makeStyles((theme) => ({
  trigger: {
    position: "relative",
    minWidth: 220,
    boxSizing: "border-box",
    borderRadius: 14,
    padding: "12.5px 32px 12.5px 16px",
    justifyContent: "flex-start",
    fontWeight: 500,
    lineHeight: 1.1876,
    border:
      theme.palette.type === "dark"
        ? "1.5px solid rgba(91, 124, 255, 0.3)"
        : "1.5px solid #e2e8f0",
    background:
      theme.palette.type === "dark"
        ? "rgba(39, 39, 42, 0.95)"
        : "#f8fafc",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      borderColor:
        theme.palette.type === "dark"
          ? "rgba(91, 124, 255, 0.7)"
          : theme.palette.primary.main,
    },
  },
  triggerOpen: {
    borderColor: theme.palette.primary.main,
    borderWidth: "2px",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 0 0 1px rgba(91, 124, 255, 0.15)"
        : "0 0 0 1px rgba(61, 92, 253, 0.1)",
  },
  triggerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.8),
    minWidth: 0,
    flex: 1,
  },
  triggerLabel: {
    display: "block",
    fontSize: "1rem",
    fontWeight: 500,
    lineHeight: 1.1876,
    color: theme.palette.type === "dark" ? "#ffffff" : "#1e293b",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "left",
  },
  placeholder: {
    color: theme.palette.type === "dark" ? "#ffffff" : "#1e293b",
  },
  triggerIcon: {
    position: "absolute",
    right: 7,
    top: "calc(50% - 12px)",
    width: 24,
    height: 24,
    pointerEvents: "none",
    color: theme.palette.type === "dark" ? "#5B7CFF" : "#64748b",
    filter:
      theme.palette.type === "dark"
        ? "drop-shadow(0 0 4px rgba(91, 124, 255, 0.4))"
        : "none",
  },
  popover: {
    "& .MuiPopover-paper": {
      width: 320,
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100vh - 48px)",
      display: "flex",
      flexDirection: "column",
      marginTop: theme.spacing(1),
      borderRadius: 20,
      overflow: "hidden",
      background: theme.palette.type === "dark" ? "#22262b" : "#ffffff",
      color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
      border:
        theme.palette.type === "dark"
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 20px 48px rgba(0, 0, 0, 0.45)"
          : "0 20px 44px rgba(15, 23, 42, 0.16)",
    },
  },
  popoverHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.75, 2, 1),
  },
  popoverTitle: {
    fontSize: "1rem",
    fontWeight: 700,
  },
  popoverClose: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  popoverBody: {
    padding: theme.spacing(0, 1.5, 1.5),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  searchField: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.7, 1.4),
    borderRadius: 16,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(248, 250, 252, 0.9)",
  },
  searchIcon: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  searchInput: {
    flex: 1,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    "& input::placeholder": {
      color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
      opacity: 1,
    },
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.25, 0.5, 1.25),
  },
  statusText: {
    fontSize: "0.95rem",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.76)" : "#6b7280",
  },
  actionButton: {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    fontSize: "0.95rem",
    color: theme.palette.primary.main,
    fontFamily: "inherit",
  },
  divider: {
    marginBottom: theme.spacing(0.75),
  },
  optionsScrollArea: {
    maxHeight: 320,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    marginRight: theme.spacing(-0.5),
  },
  option: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.1, 1.4),
    borderRadius: 14,
    marginBottom: theme.spacing(0.75),
    textAlign: "left",
    background: "transparent",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(15, 23, 42, 0.04)",
    },
  },
  optionSelected: {
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.16)"
        : "rgba(59, 130, 246, 0.14)",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.2)"
          : "rgba(59, 130, 246, 0.18)",
    },
  },
  optionLabel: {
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flex: 1,
    marginRight: theme.spacing(1),
  },
  optionCheck: {
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#7aa2ff" : theme.palette.primary.main,
    flexShrink: 0,
  },
  emptyState: {
    padding: theme.spacing(1.5, 0.5, 0.5),
    fontSize: "0.94rem",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.6)" : "#6b7280",
    textAlign: "center",
  },
}));

const CompaniesMultiSelect = ({
  companies = [],
  selectedIds = [],
  onChange,
  disabled = false,
}) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const open = Boolean(anchorEl);

  const filteredCompanies = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return companies;
    return companies.filter((c) => (c.name || "").toLowerCase().includes(q));
  }, [companies, searchTerm]);

  const selectedCount = selectedIds.length;

  const handleOpen = (event) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  const handleToggle = (id) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    onChange(next);
  };

  const handleSelectAll = () => {
    onChange(filteredCompanies.map((c) => c.id));
  };

  const handleClear = () => {
    onChange([]);
  };

  const triggerLabel = useMemo(() => {
    if (selectedCount === 0) return "Empresas";
    if (selectedCount === 1) {
      const c = companies.find((x) => x.id === selectedIds[0]);
      return c?.name || "1 empresa";
    }
    return `${selectedCount} empresas`;
  }, [selectedCount, selectedIds, companies]);

  const statusLabel = useMemo(() => {
    if (selectedCount === 0) return "Mostrando todas";
    if (selectedCount === 1) return "1 selecionada";
    return `${selectedCount} selecionadas`;
  }, [selectedCount]);

  return (
    <>
      <ButtonBase
        className={`${classes.trigger} ${open ? classes.triggerOpen : ""}`}
        onClick={handleOpen}
        disabled={disabled}
      >
        <div className={classes.triggerContent}>
          <Typography
            className={`${classes.triggerLabel} ${
              selectedCount === 0 ? classes.placeholder : ""
            }`}
          >
            {triggerLabel}
          </Typography>
        </div>
        <ArrowDropDownIcon className={classes.triggerIcon} />
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.popover}
      >
        <div className={classes.popoverHeader}>
          <Typography className={classes.popoverTitle}>Empresas</Typography>
          <IconButton
            size="small"
            className={classes.popoverClose}
            onClick={handleClose}
          >
            <X size={18} />
          </IconButton>
        </div>

        <div className={classes.popoverBody}>
          <div className={classes.searchField}>
            <Search className={classes.searchIcon} />
            <InputBase
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar empresa..."
              className={classes.searchInput}
              autoFocus
            />
          </div>

          <div className={classes.statusRow}>
            <Typography className={classes.statusText}>{statusLabel}</Typography>
            {selectedCount > 0 ? (
              <button
                type="button"
                className={classes.actionButton}
                onClick={handleClear}
              >
                Limpar
              </button>
            ) : filteredCompanies.length > 0 ? (
              <button
                type="button"
                className={classes.actionButton}
                onClick={handleSelectAll}
              >
                Selecionar todas
              </button>
            ) : null}
          </div>

          <Divider className={classes.divider} />

          <div className={classes.optionsScrollArea}>
            {filteredCompanies.length === 0 ? (
              <Typography className={classes.emptyState}>
                Nenhuma empresa encontrada
              </Typography>
            ) : (
              filteredCompanies.map((c) => {
                const isSelected = selectedIds.includes(c.id);
                return (
                  <ButtonBase
                    key={c.id}
                    className={`${classes.option} ${
                      isSelected ? classes.optionSelected : ""
                    }`}
                    onClick={() => handleToggle(c.id)}
                  >
                    <Typography className={classes.optionLabel} title={c.name}>
                      {c.name}
                    </Typography>
                    {isSelected && <Check className={classes.optionCheck} />}
                  </ButtonBase>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default CompaniesMultiSelect;
